/**
 * 从 Performance API 采集整页冷启各阶段耗时（ms，相对 navigationStart）
 *
 * @description
 * 兼容 iOS 13+ / Android 8+：Level 2 navigation → performance.timing 降级；
 * FP/FCP 不可用时返回 null，不上报对应字段。
 *
 * TODO(perf-compat): fp/fcp 依赖 Paint Timing，iOS 13 部分 WebView 不支持；可参考 dcl_duration、raf_paint_duration（performance-log/h5）
 */

export interface IColdNavigationPerfSnapshot {
    dns_duration: number | null;
    ssl_duration: number | null;
    tcp_duration: number | null;
    ttfb_duration: number | null;
    response_duration: number | null;
    dom_interactive_duration: number | null;
    dcl_duration: number | null;
    load_event_duration: number | null;
    /** TODO(perf-compat): 依赖 Paint Timing，iOS 13 部分 WebView 不支持 */
    fp_duration: number | null;
    /** TODO(perf-compat): 依赖 Paint Timing，iOS 13 部分 WebView 不支持 */
    fcp_duration: number | null;
}

function positiveDelta(end: number, start: number): number | null {
    if (!Number.isFinite(end) || !Number.isFinite(start) || end <= 0 || start <= 0 || end < start) {
        return null;
    }
    return Math.round(end - start);
}

function fromNavStart(value: number, navigationStart: number): number | null {
    if (!Number.isFinite(value) || !Number.isFinite(navigationStart) || value <= 0 || navigationStart <= 0) {
        return null;
    }
    const delta = value - navigationStart;
    return delta >= 0 ? Math.round(delta) : null;
}

/** Web 导航开始 → 首个内联 JS 执行的耗时（ms）；保留合法 0，无有效采样时返回 null。 */
export function getNavigationToJsDuration(): number | null {
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
        return null;
    }
    // 旧版 WebView 不支持 timeOrigin 时，使用 Navigation Timing 的绝对时间戳。
    const navigationStart = performance.timeOrigin > 0 ? performance.timeOrigin : performance.timing?.navigationStart;
    const firstJsTime = window.__H5R_FIRST_JS_TIME__;
    if (navigationStart == null || firstJsTime == null) {
        return null;
    }
    return fromNavStart(firstJsTime, navigationStart);
}

function collectFromNavigationTimingLevel2(nav: PerformanceNavigationTiming): IColdNavigationPerfSnapshot {
    return {
        dns_duration: positiveDelta(nav.domainLookupEnd, nav.domainLookupStart),
        ssl_duration: nav.secureConnectionStart > 0 ? positiveDelta(nav.connectEnd, nav.secureConnectionStart) : null,
        tcp_duration: positiveDelta(nav.connectEnd, nav.connectStart),
        ttfb_duration: positiveDelta(nav.responseStart, nav.requestStart),
        response_duration: positiveDelta(nav.responseEnd, nav.responseStart),
        dom_interactive_duration: positiveDelta(nav.domInteractive, nav.responseEnd),
        dcl_duration: nav.domContentLoadedEventEnd > 0 ? Math.round(nav.domContentLoadedEventEnd) : null,
        load_event_duration: nav.loadEventEnd > 0 ? Math.round(nav.loadEventEnd) : null,
        fp_duration: null,
        fcp_duration: null,
    };
}

function collectFromLegacyTiming(timing: PerformanceTiming): IColdNavigationPerfSnapshot {
    const { navigationStart } = timing;
    return {
        dns_duration: positiveDelta(timing.domainLookupEnd, timing.domainLookupStart),
        ssl_duration:
            timing.secureConnectionStart > 0 ? positiveDelta(timing.connectEnd, timing.secureConnectionStart) : null,
        tcp_duration: positiveDelta(timing.connectEnd, timing.connectStart),
        ttfb_duration: positiveDelta(timing.responseStart, timing.requestStart),
        response_duration: positiveDelta(timing.responseEnd, timing.responseStart),
        dom_interactive_duration: positiveDelta(timing.domInteractive, timing.responseEnd),
        dcl_duration: fromNavStart(timing.domContentLoadedEventEnd, navigationStart),
        load_event_duration: fromNavStart(timing.loadEventEnd, navigationStart),
        fp_duration: null,
        fcp_duration: null,
    };
}

function attachPaintTiming(snapshot: IColdNavigationPerfSnapshot): IColdNavigationPerfSnapshot {
    if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
        // TODO(perf-compat): 无 Paint Timing API，fp/fcp 无法采集
        return snapshot;
    }

    try {
        const paints = performance.getEntriesByType('paint');
        const fp = paints.find(entry => entry.name === 'first-paint');
        const fcp = paints.find(entry => entry.name === 'first-contentful-paint');
        return {
            ...snapshot,
            fp_duration: fp ? Math.round(fp.startTime) : snapshot.fp_duration,
            fcp_duration: fcp ? Math.round(fcp.startTime) : snapshot.fcp_duration,
        };
    } catch {
        return snapshot;
    }
}

/**
 * 采集当前文档冷启导航耗时快照（仅整页加载有效，SPA 切页无新 navigation 条目）
 */
export function collectColdNavigationPerfSnapshot(): IColdNavigationPerfSnapshot {
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
        return {
            dns_duration: null,
            ssl_duration: null,
            tcp_duration: null,
            ttfb_duration: null,
            response_duration: null,
            dom_interactive_duration: null,
            dcl_duration: null,
            load_event_duration: null,
            fp_duration: null,
            fcp_duration: null,
        };
    }

    try {
        if (typeof performance.getEntriesByType === 'function') {
            const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
            if (nav) {
                return attachPaintTiming(collectFromNavigationTimingLevel2(nav));
            }
        }
        const { timing } = performance;
        if (timing?.navigationStart) {
            return attachPaintTiming(collectFromLegacyTiming(timing));
        }
    } catch {
        // fall through
    }

    return attachPaintTiming({
        dns_duration: null,
        ssl_duration: null,
        tcp_duration: null,
        ttfb_duration: null,
        response_duration: null,
        dom_interactive_duration: null,
        dcl_duration: null,
        load_event_duration: null,
        fp_duration: null,
        fcp_duration: null,
    });
}
